import { createContext, useContext } from 'react';

/**
 * Provider-agnostic authentication context.
 *
 * All components should use `useAuth()` instead of provider-specific hooks
 * (not a library-specific hook). This keeps components decoupled from the
 * underlying auth implementation (Better Auth).
 */
export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: {
    name?: string;
    email?: string;
    picture?: string;
    sub?: string;
  };
  error?: Error;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Hook to access the current auth state.
 * Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
